import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AdminPageHeader, RowActions } from "@/components/admin/crud-helpers";
import {
  upsertTaxZone, deleteTaxZone,
  upsertTaxRate, deleteTaxRate,
  upsertTaxExemptGroup, addExemptMember, removeExemptMember, validateVatId,
  saveTaxSettings, saveTaxIntegration,
} from "@/lib/tax.functions";
import { CheckCircle2, Globe, Percent, Shield, FileBarChart, Plus, Trash2, FileCheck2, Landmark, Users } from "lucide-react";
import { StatsStrip } from "@/components/admin/stats-strip";

export const Route = createFileRoute("/_authenticated/admin/tax")({
  component: TaxPage,
});

type Zone = {
  id: string; name: string; country_code: string; state_codes: string[];
  postcode_patterns: string[]; priority: number; active: boolean; notes: string | null;
};
type Rate = {
  id: string; zone_id: string; name: string; kind: "vat"|"gst"|"sales_tax"|"custom";
  rate_percent: number; priority: number; compound: boolean; applies_to_shipping: boolean;
  reverse_charge: boolean; product_category_id: string | null; active: boolean;
};
type Group = { id: string; name: string; slug: string; description: string | null; requires_vat_id: boolean; active: boolean };
type Member = { id: string; group_id: string; user_id: string; vat_id: string | null; vat_validated_at: string | null; notes: string | null };

const KIND_LABELS: Record<Rate["kind"], string> = {
  vat: "VAT", gst: "GST", sales_tax: "Sales Tax", custom: "Custom",
};

function TaxPage() {
  const { data: counts } = useQuery({
    queryKey: ["tax-counts"],
    queryFn: async () => {
      const [z, r, g, m] = await Promise.all([
        supabase.from("tax_zones").select("id,active"),
        supabase.from("tax_rates").select("id,active"),
        supabase.from("tax_exempt_groups").select("id"),
        supabase.from("tax_exempt_members").select("id"),
      ]);
      return {
        zones: z.data?.length ?? 0,
        activeZones: (z.data ?? []).filter((x: any) => x.active).length,
        rates: r.data?.length ?? 0,
        activeRates: (r.data ?? []).filter((x: any) => x.active).length,
        groups: g.data?.length ?? 0,
        members: m.data?.length ?? 0,
      };
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Tax rules</h1>
        <p className="text-sm text-muted-foreground">VAT, GST, sales tax, and regional tax configuration.</p>
      </div>
      <StatsStrip
        tiles={[
          { label: "Zones", value: counts?.zones ?? 0, icon: Globe, tone: "info", hint: `${counts?.activeZones ?? 0} active` },
          { label: "Rates", value: counts?.rates ?? 0, icon: Percent, tone: "success", hint: `${counts?.activeRates ?? 0} active` },
          { label: "Exempt groups", value: counts?.groups ?? 0, icon: Shield, tone: "warning" },
          { label: "Exempt members", value: counts?.members ?? 0, icon: Users },
        ]}
      />
      <Tabs defaultValue="settings">
        <PillTabsList search className="flex flex-wrap">
          <PillTabsTrigger value="settings"><Shield className="mr-1.5 h-4 w-4" />Settings</PillTabsTrigger>
          <PillTabsTrigger value="zones"><Globe className="mr-1.5 h-4 w-4" />Zones</PillTabsTrigger>
          <PillTabsTrigger value="rates"><Percent className="mr-1.5 h-4 w-4" />Rates</PillTabsTrigger>
          <PillTabsTrigger value="exempt">Exempt groups</PillTabsTrigger>
          <PillTabsTrigger value="zatca"><FileCheck2 className="mr-1.5 h-4 w-4" />ZATCA (KSA)</PillTabsTrigger>
          <PillTabsTrigger value="bdnbr"><Landmark className="mr-1.5 h-4 w-4" />Bangladesh NBR</PillTabsTrigger>
          <PillTabsTrigger value="reports"><FileBarChart className="mr-1.5 h-4 w-4" />Reports</PillTabsTrigger>
        </PillTabsList>
        <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
        <TabsContent value="zones" className="mt-4"><ZonesTab /></TabsContent>
        <TabsContent value="rates" className="mt-4"><RatesTab /></TabsContent>
        <TabsContent value="exempt" className="mt-4"><ExemptTab /></TabsContent>
        <TabsContent value="zatca" className="mt-4"><ZatcaTab /></TabsContent>
        <TabsContent value="bdnbr" className="mt-4"><BdNbrTab /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============ SETTINGS ============ */
function SettingsTab() {
  const qc = useQueryClient();
  const saveFn = useServerFn(saveTaxSettings);
  const { data: settings } = useQuery({
    queryKey: ["tax-settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_settings").select("value").eq("key", "tax_settings").maybeSingle();
      if (error) throw error;
      return (data?.value as any) ?? null;
    },
  });
  const [form, setForm] = useState({
    base_country: "US",
    prices_include_tax: false,
    display_prices_including_tax: false,
    validate_vat_ids: false,
    enable_reverse_charge: false,
    rounding: "per_total" as "per_line" | "per_total",
  });
  useEffect(() => { if (settings) setForm({ ...form, ...settings }); /* eslint-disable-next-line */ }, [settings]);

  const save = useMutation({
    mutationFn: (v: typeof form) => saveFn({ data: v }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-settings"] }); toast.success("Settings saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  return (
    <Card>
      <CardHeader><CardTitle>Tax settings</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Base country (ISO 2)</Label>
            <Input value={form.base_country} maxLength={2}
              onChange={(e) => setForm({ ...form, base_country: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <Label>Rounding</Label>
            <Select value={form.rounding} onValueChange={(v) => setForm({ ...form, rounding: v as any })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="per_line">Round per line item</SelectItem>
                <SelectItem value="per_total">Round per order total</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <ToggleRow label="Prices include tax" desc="Catalog prices are tax-inclusive." checked={form.prices_include_tax}
          onChange={(v) => setForm({ ...form, prices_include_tax: v })} />
        <ToggleRow label="Display prices including tax" desc="Show storefront prices with tax baked in."
          checked={form.display_prices_including_tax} onChange={(v) => setForm({ ...form, display_prices_including_tax: v })} />
        <ToggleRow label="Validate B2B VAT IDs" desc="Mark customer VAT IDs as validated when accepted by admin."
          checked={form.validate_vat_ids} onChange={(v) => setForm({ ...form, validate_vat_ids: v })} />
        <ToggleRow label="Enable reverse-charge" desc="Allow zero-rated B2B EU intra-community supplies."
          checked={form.enable_reverse_charge} onChange={(v) => setForm({ ...form, enable_reverse_charge: v })} />
        <div className="flex justify-end">
          <Button onClick={() => save.mutate(form)}>Save settings</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, desc, checked, onChange }: { label: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between rounded border p-3">
      <div className="space-y-0.5">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/* ============ ZONES ============ */
function ZonesTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Zone | null>(null);
  const upsertFn = useServerFn(upsertTaxZone);
  const delFn = useServerFn(deleteTaxZone);

  const { data = [] } = useQuery({
    queryKey: ["tax-zones"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tax_zones").select("*").order("priority", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Zone[];
    },
  });
  const save = useMutation({
    mutationFn: (p: any) => upsertFn({ data: p }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-zones"] }); setOpen(false); toast.success("Zone saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-zones"] }); toast.success("Deleted"); },
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader title="Tax zones" description="Group countries, states, and postcode ranges." newLabel="New zone"
        onNew={() => { setEditing(null); setOpen(true); }} />
      <div className="rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><th className="p-2 text-left">Name</th><th className="p-2 text-left">Country</th><th className="p-2 text-left">States</th><th className="p-2 text-left">Postcodes</th><th className="p-2 text-left">Priority</th><th className="p-2 text-left">Active</th><th /></tr>
          </thead>
          <tbody>
            {data.map(z => (
              <tr key={z.id} className="border-t">
                <td className="p-2 font-medium">{z.name}</td>
                <td className="p-2 font-mono">{z.country_code}</td>
                <td className="p-2 text-xs">{z.state_codes.join(", ") || "—"}</td>
                <td className="p-2 text-xs">{z.postcode_patterns.join(", ") || "—"}</td>
                <td className="p-2">{z.priority}</td>
                <td className="p-2">{z.active ? <Badge>Active</Badge> : <Badge variant="outline">Off</Badge>}</td>
                <td className="p-2"><RowActions onEdit={() => { setEditing(z); setOpen(true); }} onDelete={() => { if (confirm("Delete zone? Rates will be removed.")) remove.mutate(z.id); }} /></td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No zones yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <ZoneDialog open={open} onOpenChange={setOpen} zone={editing} onSave={(p) => save.mutate(p)} />
    </div>
  );
}

function ZoneDialog({ open, onOpenChange, zone, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void; zone: Zone | null; onSave: (p: any) => void;
}) {
  const initial = () => zone ? {
    ...zone, state_codes: zone.state_codes.join(", "), postcode_patterns: zone.postcode_patterns.join(", "),
  } : { name: "", country_code: "", state_codes: "", postcode_patterns: "", priority: 0, active: true, notes: "" };
  const [form, setForm] = useState<any>(initial);
  useEffect(() => { setForm(initial()); /* eslint-disable-next-line */ }, [zone, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{zone ? "Edit zone" : "New zone"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Country (ISO 2)</Label><Input maxLength={2} value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })} /></div>
            <div><Label>Priority</Label><Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} /></div>
          </div>
          <div><Label>State codes (comma-separated)</Label><Input value={form.state_codes} onChange={(e) => setForm({ ...form, state_codes: e.target.value })} placeholder="CA, NY, TX" /></div>
          <div><Label>Postcode patterns (comma-separated)</Label><Input value={form.postcode_patterns} onChange={(e) => setForm({ ...form, postcode_patterns: e.target.value })} placeholder="9*, 100*" /></div>
          <div><Label>Notes</Label><Textarea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave({
            id: zone?.id,
            name: form.name, country_code: form.country_code, priority: form.priority, active: form.active,
            notes: form.notes || undefined,
            state_codes: String(form.state_codes).split(",").map((s: string) => s.trim()).filter(Boolean),
            postcode_patterns: String(form.postcode_patterns).split(",").map((s: string) => s.trim()).filter(Boolean),
          })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ RATES ============ */
function RatesTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Rate | null>(null);
  const upsertFn = useServerFn(upsertTaxRate);
  const delFn = useServerFn(deleteTaxRate);

  const { data: zones = [] } = useQuery({
    queryKey: ["tax-zones-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tax_zones").select("id,name,country_code").order("name");
      if (error) throw error;
      return (data ?? []) as Pick<Zone, "id"|"name"|"country_code">[];
    },
  });
  const { data: rates = [] } = useQuery({
    queryKey: ["tax-rates"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tax_rates").select("*").order("priority", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Rate[];
    },
  });

  const save = useMutation({
    mutationFn: (p: any) => upsertFn({ data: p }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-rates"] }); setOpen(false); toast.success("Rate saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-rates"] }); toast.success("Deleted"); },
  });

  const zoneMap = useMemo(() => new Map(zones.map(z => [z.id, z])), [zones]);

  return (
    <div className="space-y-4">
      <AdminPageHeader title="Tax rates" description="VAT / GST / sales tax per zone." newLabel="New rate"
        onNew={() => { setEditing(null); setOpen(true); }} />
      <div className="rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><th className="p-2 text-left">Name</th><th className="p-2 text-left">Zone</th><th className="p-2 text-left">Kind</th><th className="p-2 text-right">Rate</th><th className="p-2 text-left">Flags</th><th className="p-2 text-left">Active</th><th /></tr>
          </thead>
          <tbody>
            {rates.map(r => (
              <tr key={r.id} className="border-t">
                <td className="p-2 font-medium">{r.name}</td>
                <td className="p-2">{zoneMap.get(r.zone_id)?.name ?? "—"} <span className="text-xs text-muted-foreground">{zoneMap.get(r.zone_id)?.country_code}</span></td>
                <td className="p-2"><Badge variant="outline">{KIND_LABELS[r.kind]}</Badge></td>
                <td className="p-2 text-right font-mono">{Number(r.rate_percent).toFixed(3)}%</td>
                <td className="p-2 text-xs space-x-1">
                  {r.compound && <Badge variant="outline">compound</Badge>}
                  {r.applies_to_shipping && <Badge variant="outline">shipping</Badge>}
                  {r.reverse_charge && <Badge variant="outline">reverse-charge</Badge>}
                </td>
                <td className="p-2">{r.active ? <Badge>Active</Badge> : <Badge variant="outline">Off</Badge>}</td>
                <td className="p-2"><RowActions onEdit={() => { setEditing(r); setOpen(true); }} onDelete={() => { if (confirm("Delete rate?")) remove.mutate(r.id); }} /></td>
              </tr>
            ))}
            {rates.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No rates yet. Create a zone first, then add rates here.</td></tr>}
          </tbody>
        </table>
      </div>
      <RateDialog open={open} onOpenChange={setOpen} rate={editing} zones={zones} onSave={(p) => save.mutate(p)} />
    </div>
  );
}

function RateDialog({ open, onOpenChange, rate, zones, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void; rate: Rate | null;
  zones: Pick<Zone, "id"|"name"|"country_code">[]; onSave: (p: any) => void;
}) {
  const initial = () => rate ? { ...rate, rate_percent: String(rate.rate_percent) } : {
    zone_id: "", name: "", kind: "vat", rate_percent: "0", priority: 0,
    compound: false, applies_to_shipping: false, reverse_charge: false, active: true, product_category_id: null,
  };
  const [form, setForm] = useState<any>(initial);
  useEffect(() => { setForm(initial()); /* eslint-disable-next-line */ }, [rate, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{rate ? "Edit rate" : "New rate"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Zone</Label>
            <Select value={form.zone_id} onValueChange={(v) => setForm({ ...form, zone_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select zone" /></SelectTrigger>
              <SelectContent>{zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name} ({z.country_code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Standard VAT" /></div>
            <div>
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vat">VAT</SelectItem>
                  <SelectItem value="gst">GST</SelectItem>
                  <SelectItem value="sales_tax">Sales tax</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Rate (%)</Label><Input type="number" step="0.001" value={form.rate_percent} onChange={(e) => setForm({ ...form, rate_percent: e.target.value })} /></div>
            <div><Label>Priority</Label><Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} /></div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2"><Switch checked={form.compound} onCheckedChange={(v) => setForm({ ...form, compound: v })} /><Label>Compound (applied on top of other taxes)</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.applies_to_shipping} onCheckedChange={(v) => setForm({ ...form, applies_to_shipping: v })} /><Label>Applies to shipping</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.reverse_charge} onCheckedChange={(v) => setForm({ ...form, reverse_charge: v })} /><Label>Reverse-charge (zero-rate B2B)</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!form.zone_id || !form.name} onClick={() => onSave({
            id: rate?.id,
            zone_id: form.zone_id, name: form.name, kind: form.kind,
            rate_percent: parseFloat(form.rate_percent) || 0,
            priority: form.priority, compound: form.compound,
            applies_to_shipping: form.applies_to_shipping, reverse_charge: form.reverse_charge,
            active: form.active, product_category_id: form.product_category_id ?? null,
          })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ EXEMPT ============ */
function ExemptTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Group | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const upsertFn = useServerFn(upsertTaxExemptGroup);
  const addFn = useServerFn(addExemptMember);
  const removeFn = useServerFn(removeExemptMember);
  const validateFn = useServerFn(validateVatId);

  const { data: groups = [] } = useQuery({
    queryKey: ["tax-exempt-groups"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tax_exempt_groups").select("*").order("name");
      if (error) throw error;
      return (data ?? []) as Group[];
    },
  });
  const { data: members = [] } = useQuery({
    queryKey: ["tax-exempt-members", activeGroup],
    enabled: !!activeGroup,
    queryFn: async () => {
      const { data, error } = await supabase.from("tax_exempt_members").select("*").eq("group_id", activeGroup!).order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Member[];
    },
  });
  const { data: profiles = [] } = useQuery({
    queryKey: ["profiles-for-exempt"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("id,display_name").limit(200);
      return data ?? [];
    },
  });

  const save = useMutation({
    mutationFn: (p: any) => upsertFn({ data: p }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-exempt-groups"] }); setOpen(false); toast.success("Group saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const addMem = useMutation({
    mutationFn: (p: any) => addFn({ data: p }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-exempt-members", activeGroup] }); toast.success("Member added"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const rmMem = useMutation({
    mutationFn: (id: string) => removeFn({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-exempt-members", activeGroup] }); toast.success("Removed"); },
  });
  const validate = useMutation({
    mutationFn: (id: string) => validateFn({ data: { member_id: id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-exempt-members", activeGroup] }); toast.success("VAT ID validated"); },
  });

  const [newUserId, setNewUserId] = useState("");
  const [newVat, setNewVat] = useState("");

  return (
    <div className="space-y-4">
      <AdminPageHeader title="Tax-exempt groups" description="Customer groups that bypass tax (e.g. validated B2B)." newLabel="New group"
        onNew={() => { setEditing(null); setOpen(true); }} />
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-sm">Groups</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {groups.map(g => (
              <button key={g.id}
                className={`w-full rounded border px-3 py-2 text-left text-sm ${activeGroup === g.id ? "bg-accent" : ""}`}
                onClick={() => setActiveGroup(g.id)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{g.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{g.slug}</span>
                </div>
                {g.requires_vat_id && <Badge variant="outline" className="mt-1">requires VAT ID</Badge>}
              </button>
            ))}
            {groups.length === 0 && <p className="text-sm text-muted-foreground">No groups yet.</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm">Members{activeGroup ? "" : " — pick a group"}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {activeGroup && (
              <>
                <div className="space-y-1 max-h-64 overflow-auto">
                  {members.map(m => {
                    const prof = profiles.find((p: any) => p.id === m.user_id);
                    return (
                      <div key={m.id} className="flex items-center justify-between rounded border px-3 py-2 text-sm">
                        <div>
                          <div className="font-medium">{(prof as any)?.display_name ?? m.user_id.slice(0, 8)}</div>
                          <div className="text-xs text-muted-foreground">
                            {m.vat_id ? `VAT: ${m.vat_id}` : "no VAT ID"}
                            {m.vat_validated_at && <span className="ml-2 text-emerald-600 inline-flex items-center"><CheckCircle2 className="mr-0.5 h-3 w-3" />validated</span>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {m.vat_id && !m.vat_validated_at && <Button size="sm" variant="outline" onClick={() => validate.mutate(m.id)}>Validate</Button>}
                          <Button size="sm" variant="ghost" onClick={() => rmMem.mutate(m.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    );
                  })}
                  {members.length === 0 && <p className="text-sm text-muted-foreground">No members in this group.</p>}
                </div>
                <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <Select value={newUserId} onValueChange={setNewUserId}>
                    <SelectTrigger><SelectValue placeholder="User" /></SelectTrigger>
                    <SelectContent>{profiles.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.display_name ?? p.id.slice(0,8)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input placeholder="VAT ID (optional)" value={newVat} onChange={(e) => setNewVat(e.target.value)} />
                  <Button disabled={!newUserId} onClick={() => {
                    addMem.mutate({ group_id: activeGroup, user_id: newUserId, vat_id: newVat || undefined });
                    setNewUserId(""); setNewVat("");
                  }}><Plus className="h-4 w-4" /></Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <GroupDialog open={open} onOpenChange={setOpen} group={editing} onSave={(p) => save.mutate(p)} />
    </div>
  );
}

function GroupDialog({ open, onOpenChange, group, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void; group: Group | null; onSave: (p: any) => void;
}) {
  const [form, setForm] = useState<any>(group ?? { name: "", slug: "", requires_vat_id: false, active: true });
  useEffect(() => { setForm(group ?? { name: "", slug: "", requires_vat_id: false, active: true }); }, [group, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{group ? "Edit group" : "New group"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} /></div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="flex items-center gap-2"><Switch checked={form.requires_vat_id} onCheckedChange={(v) => setForm({ ...form, requires_vat_id: v })} /><Label>Requires VAT ID</Label></div>
          <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!form.name || !form.slug} onClick={() => onSave({
            id: group?.id, name: form.name, slug: form.slug,
            description: form.description || undefined, requires_vat_id: form.requires_vat_id, active: form.active,
          })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ REPORTS ============ */
function ReportsTab() {
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const { data: invoices = [] } = useQuery({
    queryKey: ["tax-report-invoices", from, to],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices").select("issue_date,total_cents,tax_cents,currency,status")
        .gte("issue_date", from).lte("issue_date", to).limit(5000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const summary = useMemo(() => {
    const paid = invoices.filter((i: any) => i.status === "paid");
    const totalTax = paid.reduce((s, i: any) => s + (i.tax_cents ?? 0), 0);
    const totalSales = paid.reduce((s, i: any) => s + (i.total_cents ?? 0), 0);
    const byCurrency = new Map<string, { tax: number; total: number; count: number }>();
    for (const i of paid as any[]) {
      const k = i.currency ?? "USD";
      const r = byCurrency.get(k) ?? { tax: 0, total: 0, count: 0 };
      r.tax += i.tax_cents ?? 0; r.total += i.total_cents ?? 0; r.count += 1;
      byCurrency.set(k, r);
    }
    return { totalTax, totalSales, count: paid.length, byCurrency };
  }, [invoices]);

  const fmt = (cents: number, cur = "USD") =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format((cents ?? 0) / 100);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-sm">Filing period</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          </div>
        </CardContent>
      </Card>
      <div className="grid gap-4 md:grid-cols-3">
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Paid invoices</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{summary.count}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Total sales</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{fmt(summary.totalSales)}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Tax collected</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{fmt(summary.totalTax)}</div></CardContent></Card>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-sm">By currency</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground"><tr><th className="p-2 text-left">Currency</th><th className="p-2 text-right">Invoices</th><th className="p-2 text-right">Sales</th><th className="p-2 text-right">Tax</th></tr></thead>
            <tbody>
              {Array.from(summary.byCurrency.entries()).map(([cur, r]) => (
                <tr key={cur} className="border-t">
                  <td className="p-2 font-mono">{cur}</td>
                  <td className="p-2 text-right">{r.count}</td>
                  <td className="p-2 text-right font-mono">{fmt(r.total, cur)}</td>
                  <td className="p-2 text-right font-mono">{fmt(r.tax, cur)}</td>
                </tr>
              ))}
              {summary.byCurrency.size === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">No paid invoices in this period.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============ ZATCA (KSA e-invoicing — Fatoora) ============ */
function useIntegration<T extends Record<string, any>>(key: string, defaults: T) {
  const qc = useQueryClient();
  const saveFn = useServerFn(saveTaxIntegration);
  const { data } = useQuery({
    queryKey: ["tax-integration", key],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_settings").select("value").eq("key", key).maybeSingle();
      if (error) throw error;
      return (data?.value as T) ?? null;
    },
  });
  const [form, setForm] = useState<T>(defaults);
  useEffect(() => { if (data) setForm({ ...defaults, ...data }); /* eslint-disable-next-line */ }, [data]);
  const save = useMutation({
    mutationFn: (v: T) => saveFn({ data: { key: key as any, value: v as any } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tax-integration", key] }); toast.success("Saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  return { form, setForm, save };
}

function ZatcaTab() {
  const { form, setForm, save } = useIntegration("zatca_settings", {
    enabled: false, environment: "sandbox", phase: "phase2",
    vat_number: "", cr_number: "", seller_name_ar: "", seller_name_en: "",
    address_street: "", address_building: "", address_district: "",
    address_city: "", address_postal: "", address_country: "SA",
    fatoora_api_url: "", csid: "", csid_secret: "",
    invoice_counter: 0, include_qr: true,
  });
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileCheck2 className="h-4 w-4" /> ZATCA Fatoora — Saudi Arabia
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            E-invoicing integration with the Zakat, Tax and Customs Authority (ZATCA).
            Phase 2 (Integration) requires onboarded CSID credentials and QR codes on every invoice.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow label="Enable ZATCA integration" desc="Submit/clear invoices via Fatoora when issuing in SA."
            checked={form.enabled} onChange={(v) => set("enabled", v)} />
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Environment</Label>
              <Select value={form.environment} onValueChange={(v) => set("environment", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox</SelectItem>
                  <SelectItem value="simulation">Simulation</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Phase</Label>
              <Select value={form.phase} onValueChange={(v) => set("phase", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="phase1">Phase 1 — Generation</SelectItem>
                  <SelectItem value="phase2">Phase 2 — Integration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fatoora API URL</Label>
              <Input value={form.fatoora_api_url} onChange={(e) => set("fatoora_api_url", e.target.value)}
                placeholder="https://gw-fatoora.zatca.gov.sa/e-invoicing/core" />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div><Label>VAT registration number (15 digits)</Label>
              <Input value={form.vat_number} maxLength={15} onChange={(e) => set("vat_number", e.target.value)} placeholder="3xxxxxxxxxxxxx3" /></div>
            <div><Label>Commercial Registration (CR) number</Label>
              <Input value={form.cr_number} onChange={(e) => set("cr_number", e.target.value)} /></div>
            <div><Label>Seller name (Arabic)</Label>
              <Input dir="rtl" value={form.seller_name_ar} onChange={(e) => set("seller_name_ar", e.target.value)} /></div>
            <div><Label>Seller name (English)</Label>
              <Input value={form.seller_name_en} onChange={(e) => set("seller_name_en", e.target.value)} /></div>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium">Registered address</div>
            <div className="grid gap-3 md:grid-cols-3">
              <div><Label>Street</Label><Input value={form.address_street} onChange={(e) => set("address_street", e.target.value)} /></div>
              <div><Label>Building no.</Label><Input value={form.address_building} onChange={(e) => set("address_building", e.target.value)} /></div>
              <div><Label>District</Label><Input value={form.address_district} onChange={(e) => set("address_district", e.target.value)} /></div>
              <div><Label>City</Label><Input value={form.address_city} onChange={(e) => set("address_city", e.target.value)} /></div>
              <div><Label>Postal code</Label><Input value={form.address_postal} onChange={(e) => set("address_postal", e.target.value)} /></div>
              <div><Label>Country (ISO 2)</Label><Input maxLength={2} value={form.address_country}
                onChange={(e) => set("address_country", e.target.value.toUpperCase())} /></div>
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium">Onboarding credentials (CSID)</div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2"><Label>Compliance CSID (binary base64)</Label>
                <Textarea rows={3} value={form.csid} onChange={(e) => set("csid", e.target.value)} placeholder="Returned by ZATCA after compliance request" /></div>
              <div className="md:col-span-2"><Label>CSID secret</Label>
                <Textarea rows={2} value={form.csid_secret} onChange={(e) => set("csid_secret", e.target.value)} /></div>
              <div><Label>Invoice counter (ICV)</Label>
                <Input type="number" value={form.invoice_counter}
                  onChange={(e) => set("invoice_counter", parseInt(e.target.value) || 0)} /></div>
            </div>
          </div>

          <ToggleRow label="Include TLV QR code" desc="Embed the ZATCA-compliant QR on PDF invoices."
            checked={form.include_qr} onChange={(v) => set("include_qr", v)} />

          <div className="flex justify-end">
            <Button onClick={() => save.mutate(form)} disabled={save.isPending}>Save ZATCA settings</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============ Bangladesh NBR (Mushak / VAT) ============ */
function BdNbrTab() {
  const { form, setForm, save } = useIntegration("bd_nbr_settings", {
    enabled: false, environment: "sandbox",
    bin: "", nbr_reg_no: "", mushak_form: "6.3",
    vat_rate_percent: 15, supplementary_duty_percent: 0,
    vds_enabled: false, vds_percent: 0,
    fiscal_device_id: "", invoice_prefix: "MUSHAK",
    nbr_api_url: "", nbr_api_token: "",
    bn_invoice_language: true,
  });
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="h-4 w-4" /> Bangladesh NBR — Mushak VAT
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            National Board of Revenue (NBR) VAT integration. Issues Mushak 6.3 / 6.6 / 9.1 compliant
            invoices and supports VAT Deducted at Source (VDS) and Supplementary Duty (SD).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow label="Enable Bangladesh NBR integration"
            desc="Apply BD VAT rules and issue Mushak-compliant invoices."
            checked={form.enabled} onChange={(v) => set("enabled", v)} />

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Environment</Label>
              <Select value={form.environment} onValueChange={(v) => set("environment", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mushak form</Label>
              <Select value={form.mushak_form} onValueChange={(v) => set("mushak_form", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="6.3">6.3 — Tax invoice</SelectItem>
                  <SelectItem value="6.6">6.6 — Turnover tax</SelectItem>
                  <SelectItem value="9.1">9.1 — VAT return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice prefix</Label>
              <Input value={form.invoice_prefix} onChange={(e) => set("invoice_prefix", e.target.value.toUpperCase())} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div><Label>Business Identification Number (BIN)</Label>
              <Input value={form.bin} onChange={(e) => set("bin", e.target.value)} placeholder="13 digits" /></div>
            <div><Label>NBR registration no.</Label>
              <Input value={form.nbr_reg_no} onChange={(e) => set("nbr_reg_no", e.target.value)} /></div>
            <div><Label>Standard VAT rate (%)</Label>
              <Input type="number" step="0.01" value={form.vat_rate_percent}
                onChange={(e) => set("vat_rate_percent", parseFloat(e.target.value) || 0)} /></div>
            <div><Label>Supplementary duty (%)</Label>
              <Input type="number" step="0.01" value={form.supplementary_duty_percent}
                onChange={(e) => set("supplementary_duty_percent", parseFloat(e.target.value) || 0)} /></div>
            <div><Label>Fiscal device ID (EFD/SDC)</Label>
              <Input value={form.fiscal_device_id} onChange={(e) => set("fiscal_device_id", e.target.value)} /></div>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium">VAT Deducted at Source (VDS)</div>
            <div className="grid gap-3 md:grid-cols-2">
              <ToggleRow label="Enable VDS" desc="Withhold VAT on qualifying B2B supplies."
                checked={form.vds_enabled} onChange={(v) => set("vds_enabled", v)} />
              <div><Label>VDS rate (%)</Label>
                <Input type="number" step="0.01" value={form.vds_percent}
                  onChange={(e) => set("vds_percent", parseFloat(e.target.value) || 0)} /></div>
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium">NBR API</div>
            <div className="grid gap-3">
              <div><Label>API URL</Label>
                <Input value={form.nbr_api_url} onChange={(e) => set("nbr_api_url", e.target.value)}
                  placeholder="https://vat.gov.bd/api" /></div>
              <div><Label>API token</Label>
                <Textarea rows={2} value={form.nbr_api_token}
                  onChange={(e) => set("nbr_api_token", e.target.value)} /></div>
            </div>
          </div>

          <ToggleRow label="Bilingual invoices (Bangla + English)"
            desc="Include Bangla labels on Mushak invoices alongside English."
            checked={form.bn_invoice_language} onChange={(v) => set("bn_invoice_language", v)} />

          <div className="flex justify-end">
            <Button onClick={() => save.mutate(form)} disabled={save.isPending}>Save Bangladesh NBR settings</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
